import { mediaList } from "@/mocks/media";
import { categoryList } from "@/mocks/banners";
import type { MediaItem } from "@/types/media";

export const PAGE_SIZE = 20;

export const regionOptions = [
  "全部",
  "中国大陆",
  "美国",
  "日本",
  "韩国",
  "英国",
  "中国香港",
  "法国",
];

export const yearOptions = ["全部", "2026", "2025", "2024", "2023"];

export const ratingOptions = [
  { label: "全部", value: 0 },
  { label: "9.0 分以上", value: 9 },
  { label: "8.5 分以上", value: 8.5 },
  { label: "8.0 分以上", value: 8 },
  { label: "7.5 分以上", value: 7.5 },
];

export const sortOptions = [
  { key: "recommend", label: "推荐权重" },
  { key: "heat", label: "热度" },
  { key: "year", label: "上映时间" },
  { key: "rating", label: "评分" },
] as const;

export type SortKey = (typeof sortOptions)[number]["key"];

const genreKeyMap: Record<string, string> = {
  "sci-fi": "科幻",
  suspense: "悬疑",
  drama: "剧情",
  fantasy: "奇幻",
  action: "动作",
  romance: "爱情",
  comedy: "喜剧",
  history: "历史",
};

export function getMediaById(id: string | undefined): MediaItem | undefined {
  if (!id) return undefined;
  return mediaList.find((item) => item.id === id);
}

export function getCategoryMeta(key: string) {
  return categoryList.find((c) => c.key === key);
}

/** Resolve a category route param to a filtered media list */
export function filterByCategory(key: string): MediaItem[] {
  if (key === "movie" || key === "tv") {
    return mediaList.filter((item) => item.category === key);
  }
  const genre = genreKeyMap[key];
  if (genre) {
    return mediaList.filter((item) => item.genres.includes(genre));
  }
  return [];
}

export interface FilterState {
  region: string;
  year: string;
  minRating: number;
}

export function applyFilters(
  list: MediaItem[],
  filters: FilterState
): MediaItem[] {
  return list.filter((item) => {
    if (filters.region !== "全部" && item.region !== filters.region) return false;
    if (filters.year !== "全部" && String(item.year) !== filters.year)
      return false;
    if (filters.minRating > 0 && item.rating < filters.minRating) return false;
    return true;
  });
}

export function sortMedia(list: MediaItem[], key: SortKey): MediaItem[] {
  const copy = [...list];
  switch (key) {
    case "heat":
      return copy.sort((a, b) => b.heat - a.heat);
    case "year":
      return copy.sort((a, b) => b.year - a.year || b.heat - a.heat);
    case "rating":
      return copy.sort((a, b) => b.rating - a.rating);
    case "recommend":
    default:
      return copy.sort((a, b) => b.recommendWeight - a.recommendWeight);
  }
}

export function searchMedia(keyword: string): MediaItem[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return mediaList.filter((item) => {
    const haystack = [
      item.title,
      item.originalTitle,
      item.director,
      item.region,
      ...item.genres,
      ...item.cast,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function getRelated(item: MediaItem, limit = 6): MediaItem[] {
  let related = mediaList.filter(
    (m) =>
      m.id !== item.id &&
      (m.category === item.category ||
        m.genres.some((g) => item.genres.includes(g)))
  );
  if (related.length < limit) {
    const extra = mediaList.filter(
      (m) => m.id !== item.id && !related.includes(m)
    );
    related = related.concat(extra);
  }
  return related.slice(0, limit);
}

export function getHotMedia(limit = 8): MediaItem[] {
  return [...mediaList].sort((a, b) => b.heat - a.heat).slice(0, limit);
}

export function getLatestMedia(limit = 12): MediaItem[] {
  return [...mediaList]
    .sort((a, b) => b.releaseDate.localeCompare(a.releaseDate))
    .slice(0, limit);
}

export function getEditorPicks(limit = 6): MediaItem[] {
  return [...mediaList]
    .sort((a, b) => b.recommendWeight - a.recommendWeight)
    .slice(0, limit);
}

export function categoryName(category: MediaItem["category"]): string {
  return category === "movie" ? "电影" : "电视剧";
}